import Phaser from "phaser";

export interface MountDomOptions {
  clickThrough?: boolean;
}

const sceneDom = new WeakMap<Phaser.Scene, Phaser.GameObjects.DOMElement>();

export interface BodyOverlay {
  root: HTMLElement;
  destroy(): void;
}

export function showToast(message: string, kind: "error" | "info" = "error", durationMs = 3000): void {
  const t = document.createElement("div");
  t.textContent = message;
  const bg = kind === "error" ? "#7f1d1d" : "#1e293b";
  t.style.cssText = `position:fixed;top:1.5rem;left:50%;transform:translateX(-50%);background:${bg};color:#fff;padding:0.75rem 1.25rem;border-radius:0.5rem;z-index:9999;font:600 0.95rem system-ui,sans-serif;box-shadow:0 8px 24px rgba(0,0,0,0.4);opacity:0;transition:opacity 0.2s;`;
  document.body.appendChild(t);
  requestAnimationFrame(() => { t.style.opacity = "1"; });
  window.setTimeout(() => {
    t.style.opacity = "0";
    window.setTimeout(() => t.remove(), 250);
  }, durationMs);
}

export function mountBodyOverlay(scene: Phaser.Scene, html: string): BodyOverlay {
  const wrap = document.createElement("div");
  wrap.className = "dom-body-overlay";
  wrap.style.position = "fixed";
  wrap.style.inset = "0";
  wrap.style.zIndex = "100";
  wrap.innerHTML = html;
  document.body.appendChild(wrap);

  const setVisible = (v: boolean) => { wrap.style.display = v ? "" : "none"; };
  const onSleep = () => setVisible(false);
  const onWake = () => setVisible(true);
  const remove = () => {
    scene.events.off(Phaser.Scenes.Events.SLEEP, onSleep);
    scene.events.off(Phaser.Scenes.Events.WAKE, onWake);
    if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
  };
  scene.events.on(Phaser.Scenes.Events.SLEEP, onSleep);
  scene.events.on(Phaser.Scenes.Events.WAKE, onWake);
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, remove);

  return { root: wrap, destroy: remove };
}

function hardDestroy(dom?: Phaser.GameObjects.DOMElement) {
  if (!dom) return;
  const node = dom.node as HTMLElement | undefined;
  try { dom.destroy(); } catch { /* already destroyed */ }
  if (node?.parentNode) node.parentNode.removeChild(node);
}

export function mountDom(scene: Phaser.Scene, html: string, opts: MountDomOptions = {}): Phaser.GameObjects.DOMElement {
  hardDestroy(sceneDom.get(scene));

  const dom = scene.add.dom(0, 0).setOrigin(0, 0).createFromHTML(html);
  const node = dom.node as HTMLElement;
  node.style.width = `${scene.scale.width}px`;
  node.style.height = `${scene.scale.height}px`;
  if (opts.clickThrough) (dom as unknown as { pointerEvents: string }).pointerEvents = "none";

  sceneDom.set(scene, dom);

  const setNodeDisplay = (visible: boolean) => {
    const current = sceneDom.get(scene);
    const n = current?.node as HTMLElement | undefined;
    if (n) n.style.display = visible ? "" : "none";
  };
  const onSleep = () => setNodeDisplay(false);
  const onWake = () => setNodeDisplay(true);
  const onPause = () => setNodeDisplay(false);
  const onResume = () => setNodeDisplay(true);
  scene.events.on(Phaser.Scenes.Events.SLEEP, onSleep);
  scene.events.on(Phaser.Scenes.Events.WAKE, onWake);
  scene.events.on(Phaser.Scenes.Events.PAUSE, onPause);
  scene.events.on(Phaser.Scenes.Events.RESUME, onResume);

  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    scene.events.off(Phaser.Scenes.Events.SLEEP, onSleep);
    scene.events.off(Phaser.Scenes.Events.WAKE, onWake);
    scene.events.off(Phaser.Scenes.Events.PAUSE, onPause);
    scene.events.off(Phaser.Scenes.Events.RESUME, onResume);
    hardDestroy(sceneDom.get(scene));
    sceneDom.delete(scene);
  });

  return dom;
}

interface DomOverlayOptions {
  title: string;
  subtitle?: string;
  bodyHtml: string;
  onClose: () => void;
  variant?: "panel" | "fullscreen";
  inBody?: boolean;
}

export interface DomOverlay {
  dom?: Phaser.GameObjects.DOMElement;
  root: HTMLElement;
  body: HTMLElement;
  destroy(): void;
}

export function mountOverlay(scene: Phaser.Scene, opts: DomOverlayOptions): DomOverlay {
  const variant = opts.variant ?? "panel";
  const html =
    variant === "panel"
      ? `
        <div class="dom-overlay">
          <div class="dom-panel">
            <div class="dom-panel__header">
              <h1 class="dom-panel__title">${opts.title}</h1>
              ${opts.subtitle ? `<p class="dom-panel__subtitle">${opts.subtitle}</p>` : ""}
              <button class="dom-panel__close" data-overlay-close>✕</button>
            </div>
            <div class="dom-panel__body">${opts.bodyHtml}</div>
          </div>
        </div>
      `
      : `
        <div class="dom-fullscreen">
          <div class="dom-panel__header">
            <h1 class="dom-panel__title">${opts.title}</h1>
            ${opts.subtitle ? `<p class="dom-panel__subtitle">${opts.subtitle}</p>` : ""}
            <button class="dom-panel__close" data-overlay-close>✕</button>
          </div>
          <div class="dom-panel__body">${opts.bodyHtml}</div>
        </div>
      `;

  if (opts.inBody) {
    const body = mountBodyOverlay(scene, html);
    const inner = body.root.querySelector(".dom-panel__body") as HTMLElement;
    body.root.querySelectorAll("[data-overlay-close]").forEach((btn) => {
      btn.addEventListener("click", () => opts.onClose());
    });
    return { root: body.root, body: inner, destroy: body.destroy };
  }

  const dom = mountDom(scene, html);
  const root = dom.node as HTMLElement;
  const body = root.querySelector(".dom-panel__body") as HTMLElement;

  root.querySelectorAll("[data-overlay-close]").forEach((btn) => {
    btn.addEventListener("click", () => opts.onClose());
  });

  return { dom, root, body, destroy: () => dom.destroy() };
}
